import type { MeResponse } from '@nolens/domain';
import { cx, useLocale } from '@nolens/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { shellText } from '../i18n/shell';
import { CheckIcon } from './Icons';
import { useExtensionState } from './RecordDialog';
import { useShell } from './shellContext';

interface StartStep {
  key: string;
  label: string;
  done: boolean;
  /** Para onde leva; sem endereço, abre «Gravar». */
  to?: string;
}

/**
 * Primeiros passos, com o estado real da conta: extensão instalada e ligada, primeiro guia,
 * equipa e alguém convidado (fora do Grátis) e kit de marca. Some quando está tudo feito.
 */
export function useGettingStarted(me: MeResponse) {
  const t = shellText(useLocale());
  const ext = useExtensionState(me.user.email);
  const teams = me.workspaces.filter((w) => w.kind === 'team');
  const ownTeam = teams.find((w) => w.role === 'owner');
  const brandSpace = ownTeam?.id ?? me.personalWorkspaceId;
  // Numa conta grátis, as equipas são do plano Equipa (pago): não são primeiros passos
  const free = me.workspaces.find((w) => w.id === me.personalWorkspaceId)?.plan === 'free';
  const steps: StartStep[] = [
    { key: 'install', label: t.stepInstall, done: !!ext && ext !== 'missing', to: '/extensao' },
    { key: 'link', label: t.stepLink, done: ext === 'ready', to: '/extensao?ligar=1' },
    { key: 'record', label: t.stepRecord, done: me.workspaces.some((w) => w.guides > 0) },
    ...(free
      ? []
      : [
          { key: 'team', label: t.stepTeam, done: teams.length > 0, to: '/equipas/nova' },
          {
            key: 'invite',
            label: t.stepInvite,
            done: teams.some((w) => w.members > 1),
            to: ownTeam ? `/w/${ownTeam.id}/membros` : '/equipas/nova',
          },
        ]),
    {
      key: 'brand',
      label: t.stepBrand,
      done: me.workspaces.some((w) => !!w.accent),
      to: `/w/${brandSpace}/marca`,
    },
  ];
  const done = steps.filter((s) => s.done).length;
  // Enquanto procura a extensão, não mostra um progresso que vai mudar já a seguir.
  return { steps, done, total: steps.length, ready: ext !== null };
}

/** Anel de progresso no teal dos nós Nolens. */
function Ring({ value, max, size = 22 }: { value: number; max: number; size?: number }) {
  const r = (size - 4) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--border)"
        strokeWidth="3"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--nolens-bright)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${(value / max) * c} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

function StepList({
  steps,
  onPick,
  className,
}: {
  steps: StartStep[];
  onPick?: () => void;
  className?: string;
}) {
  const t = shellText(useLocale());
  const { openRecord } = useShell();
  return (
    <ul className={cx('grid gap-0.5', className)}>
      {steps.map((s) => {
        const body = (
          <>
            <span
              className={cx(
                'inline-flex size-5 shrink-0 items-center justify-center rounded-full',
                s.done
                  ? 'bg-[var(--nolens-bright)] text-[#0f2f29]'
                  : 'shadow-[inset_0_0_0_1.5px_var(--border-strong)]',
              )}
            >
              {s.done && <CheckIcon size={13} strokeWidth={2.6} />}
            </span>
            <span className={cx('flex-1', s.done && 'text-text-3 line-through')}>{s.label}</span>
            {s.done && <span className="sr-only">{t.done}</span>}
          </>
        );
        const cls =
          'flex w-full items-center gap-2.5 rounded-[8px] px-2 py-1.5 text-left text-[13.5px] hover:bg-surface-2';
        return (
          <li key={s.key} data-start-step={s.key} data-done={s.done || undefined}>
            {s.to ? (
              <Link to={s.to} className={cls} onClick={onPick}>
                {body}
              </Link>
            ) : (
              <button
                type="button"
                className={cls}
                onClick={() => {
                  onPick?.();
                  openRecord();
                }}
              >
                {body}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Na barra lateral: o anel e «3 de 6»; abre a lista por cima. */
export function GettingStartedWidget({ me }: { me: MeResponse }) {
  const t = shellText(useLocale());
  const g = useGettingStarted(me);
  const [open, setOpen] = useState(false);
  if (!g.ready || g.done === g.total) return null;
  return (
    <div data-getting-started={`${g.done}/${g.total}`}>
      {open && (
        <div className="mb-1 rounded-[12px] bg-surface-0 p-1.5 shadow-[inset_0_0_0_1px_var(--border)]">
          <StepList steps={g.steps} />
        </div>
      )}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2.5 rounded-[10px] px-2 py-2 text-left hover:bg-surface-2"
      >
        <Ring value={g.done} max={g.total} />
        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">
          {t.gettingStarted}
        </span>
        <span className="shrink-0 text-[12.5px] text-text-3">{t.progress(g.done, g.total)}</span>
      </button>
    </div>
  );
}

/** No Início: a mesma lista num cartão, enquanto houver passos por fazer. */
export function GettingStartedCard({ me }: { me: MeResponse }) {
  const t = shellText(useLocale());
  const g = useGettingStarted(me);
  if (!g.ready || g.done === g.total) return null;
  return (
    <section
      aria-labelledby="getting-started"
      className="rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
    >
      <div className="flex items-center gap-3">
        <Ring value={g.done} max={g.total} size={30} />
        <div className="min-w-0 flex-1">
          <h2 id="getting-started" className="font-semibold">
            {t.gettingStarted}
          </h2>
          <p className="text-[13px] text-text-2">{t.gettingStartedHint}</p>
        </div>
        <span className="text-sm text-text-3">{t.progress(g.done, g.total)}</span>
      </div>
      <StepList steps={g.steps} className="mt-3 gap-x-6 sm:grid-cols-2" />
    </section>
  );
}
